import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectionAnalysis } from '@/core/hooks/useSelectionAnalysis';
import { type SelectionMap } from '../FilterSelector/types';
import './SelectionSummary.scss';

interface SelectionSummaryProps {
  selection?: SelectionMap;
  items?: unknown[];
  availableItemsCount?: number;
}

export const SelectionSummary: React.FC<SelectionSummaryProps> = ({
  selection,
  items,
  availableItemsCount,
}) => {
  const { t } = useTranslation();

  // Always call hooks at the top level
  const { allAdd, allRemove, selectedCount, excludedCount } = useSelectionAnalysis(
    selection || {},
    availableItemsCount || 0,
  );

  // Handle array-based items (like locations)
  if (items !== undefined) {
    const count = items.length;
    if (count === 0) {
      return null;
    }
    return (
      <div className="selection-summary">
        <span className="selection-summary__text">
          {t('selectionSummary.selected', { count })}
        </span>
      </div>
    );
  }

  // Handle SelectionMap-based selection (like channels and languages)
  if (!selection) {
    return null;
  }

  // Don't show if "Todos" chip is displayed (all selected or all excluded)
  if (allAdd || allRemove) {
    return null;
  }

  const parts: string[] = [];
  if (selectedCount > 0) {
    parts.push(t('selectionSummary.selected', { count: selectedCount }));
  }
  if (excludedCount > 0) {
    parts.push(t('selectionSummary.excluded', { count: excludedCount }));
  }

  if (parts.length === 0) {
    return null;
  }

  return (
    <div className="selection-summary">
      <span className="selection-summary__text">{parts.join(', ')}</span>
    </div>
  );
};
